import { useState, type ReactNode } from 'react';
import { RepoAutocomplete, type RepoSuggestion } from './RepoAutocomplete';

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'https://api.repolens.awsprinl1np.click';

interface OnboardFormProps {
  onSuccess: () => void;
}

/**
 * S-ADMINLOOKUP-001: queries GET /api/search/repositories?q=<value> and maps
 * the response into RepoSuggestion[] for RepoAutocomplete. A network error or
 * non-200 response is surfaced as a rejected promise, which RepoAutocomplete
 * treats as a non-blocking lookup failure (AC-12).
 */
async function fetchRepoSuggestions(query: string): Promise<RepoSuggestion[]> {
  const res = await fetch(
    `${API_BASE}/api/search/repositories?q=${encodeURIComponent(query)}`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  const body = (await res.json()) as {
    repositories?: Array<{ fullName: string; defaultBranch?: string }>;
  };
  return (body.repositories ?? []).map((r) => ({
    fullName: r.fullName,
    defaultBranch: r.defaultBranch ?? 'main',
  }));
}

/**
 * S-UI-002: Onboard repository form extracted from App.tsx.
 * All preserved testids: onboard-repo-input, onboard-branch-input,
 * onboard-submit-btn, onboard-success, onboard-error.
 * No heading element inside this component (heading removed per redesign).
 *
 * S-ADMINLOOKUP-001: the repo field is now augmented with RepoAutocomplete
 * (lookup/autocomplete + branch auto-fill). `branchManuallyEdited` is owned
 * here, in the parent, per ADR-S-ADMINLOOKUP-001 Decision 2 — RepoAutocomplete
 * only emits onSelect(repo) and never reads/writes this flag.
 */
export function OnboardForm({ onSuccess }: OnboardFormProps): ReactNode {
  const [onboardRepo, setOnboardRepo] = useState('');
  const [onboardBranch, setOnboardBranch] = useState('main');
  const [onboardState, setOnboardState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [onboardMessage, setOnboardMessage] = useState('');
  // AC-7: false initially; set true on any manual keystroke in the branch
  // field; reset to false only by a fresh autofill from a new selection.
  const [branchManuallyEdited, setBranchManuallyEdited] = useState(false);

  const handleBranchChange = (newValue: string): void => {
    setOnboardBranch(newValue);
    setBranchManuallyEdited(true); // AC-7
  };

  const handleRepoSelect = (repo: RepoSuggestion): void => {
    if (!branchManuallyEdited) {
      // AC-6/AC-7: programmatic autofill — flag stays false, this is not a user edit.
      setOnboardBranch(repo.defaultBranch);
    }
    // AC-7: if branchManuallyEdited is true, the current branch value is
    // preserved — no-op here.
  };

  const handleOnboard = async () => {
    if (!onboardRepo.trim()) return;
    const parts = onboardRepo.trim().split('/');
    if (parts.length < 2 || !parts[0] || !parts[1]) {
      setOnboardMessage('Format: org/repo');
      setOnboardState('error');
      return;
    }
    const [org, repo] = parts;
    setOnboardState('loading');
    try {
      const res = await fetch(`${API_BASE}/repositories/${org}/${repo}/onboard`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'RepoLens-UI/1.0',
        },
        body: JSON.stringify({
          branch: onboardBranch || 'main',
          defaultBranch: onboardBranch || 'main',
        }),
      });
      if (res.ok) {
        setOnboardState('success');
        setOnboardMessage(`Ingestion started for ${onboardRepo}`);
        onSuccess();
      } else {
        const text = await res.text();
        setOnboardState('error');
        setOnboardMessage(
          res.status === 500 && text.includes('PAT')
            ? 'GHE PAT not configured — contact admin'
            : `Error ${res.status}: ${text.substring(0, 100)}`,
        );
      }
    } catch {
      setOnboardState('error');
      setOnboardMessage('Network error — ensure you are signed in');
    }
  };

  return (
    <div className="onboard-form" data-testid="onboard-form">
      <div className="onboard-form__fields">
        <RepoAutocomplete
          value={onboardRepo}
          onChange={setOnboardRepo}
          onSelect={handleRepoSelect}
          fetchSuggestions={fetchRepoSuggestions}
        />
        <input
          className="onboard-branch-input"
          data-testid="onboard-branch-input"
          placeholder="branch (default: main)"
          value={onboardBranch}
          onChange={e => handleBranchChange(e.target.value)}
          aria-label="Branch"
        />
        <button
          type="button"
          className="onboard-submit-btn"
          data-testid="onboard-submit-btn"
          onClick={handleOnboard}
          disabled={onboardState === 'loading'}
        >
          {onboardState === 'loading' ? 'Starting...' : 'Start Ingestion'}
        </button>
      </div>
      {onboardState === 'success' && (
        <div className="onboard-success" data-testid="onboard-success">
          {onboardMessage}
        </div>
      )}
      {onboardState === 'error' && (
        <div className="onboard-error" data-testid="onboard-error">
          {onboardMessage}
        </div>
      )}
    </div>
  );
}
